import {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  type ComponentType,
} from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, CircularProgress } from '@mui/material'
import { COLORS } from './theme'
import OnboardingStepperBanner from './OnboardingStepperBanner'
import OnboardingNavButtons from './OnboardingNavButtons'
import OnboardingExitDialog from './OnboardingExitDialog'
import DoodleSpinner from './components/DoodleSpinner'
import {
  STEPS,
  SEGMENTS,
  getCardTier,
  getContentWidth,
  stepBefore,
} from './onboardingSteps'
import { INITIAL_WIZARD_DATA, type WizardData } from './types'
import { OnboardingV2Provider, useOnboardingV2 } from './OnboardingContext'
import {
  writeWizardData,
  clearWizardData,
  useWizardData,
} from './hooks/useWizardData'
import { useOnboardingProgress } from './hooks/useOnboardingProgress'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { peekPostLoginRedirect } from '@/utils/postLoginRedirect'
import { getEffectiveOrgUuid, isFreeTier } from '@/utils/utils'
import ErrorMessage from '@/components/ErrorMessage'
import type { UUID } from 'crypto'
import IntroVideo from './steps/IntroVideo'
import IntroWelcome from './steps/IntroWelcome'
import OrgEntityType from './steps/OrgEntityType'
import OrgNumber from './steps/OrgNumber'
import OrgDetails from './steps/OrgDetails'
import UploadResources from './steps/UploadResources'
import UploadWebpages from './steps/UploadWebpages'
import UploadAnythingElse from './steps/UploadAnythingElse'
import FundingSupportDocs from './steps/FundingSupportDocs'
import ProfileGenerating from './steps/ProfileGenerating'
import ProfileReview from './steps/ProfileReview'
import ProfileMilestone from './steps/ProfileMilestone'
import ScanRunning from './steps/ScanRunning'
import ScanMilestone from './steps/ScanMilestone'
import ScanResults from './steps/ScanResults'
import DraftGenerating from './steps/DraftGenerating'
import FundingCreate from './steps/FundingCreate'
import FundingGuidedStep from './steps/FundingGuidedStep'
import FundingDetails from './steps/FundingDetails'
import DraftComplete from './steps/DraftComplete'
import OnboardingSubscribeBanner from './components/OnboardingSubscribeBanner'
import TermsAndConditionsBanner from '@/components/TermsAndConditionsBanner'
import SubscribeDialog from './components/SubscribeDialog'
import TeammateDialog from './components/TeammateDialog'
import { LogoutButton } from '@/components/AuthButtons'

// Dev-only QuickNav — tree-shaken from production builds
const OnboardingQuickNav = lazy(() => import('./OnboardingQuickNav'))

// OrgDetails renders across two onboarding steps via its `section` prop: name +
// website (ORG_DETAILS) and funding preferences + locations (ORG_LOCATIONS).
const OrgLocationsStep: ComponentType = () => <OrgDetails section="locations" />

// Step map — every step wired to a real component
const STEP_MAP: Record<number, ComponentType> = {
  [STEPS.INTRO_VIDEO]: IntroVideo,
  [STEPS.INTRO_WELCOME]: IntroWelcome,
  [STEPS.ORG_ENTITY_TYPE]: OrgEntityType,
  [STEPS.ORG_NUMBER]: OrgNumber,
  [STEPS.ORG_DETAILS]: OrgDetails,
  [STEPS.ORG_LOCATIONS]: OrgLocationsStep,
  [STEPS.UPLOAD_RESOURCES]: UploadResources,
  [STEPS.UPLOAD_WEBPAGES]: UploadWebpages,
  [STEPS.UPLOAD_ANYTHING_ELSE]: UploadAnythingElse,
  [STEPS.PROFILE_GENERATING]: ProfileGenerating,
  [STEPS.PROFILE_REVIEW]: ProfileReview,
  [STEPS.PROFILE_MILESTONE]: ProfileMilestone,
  [STEPS.FUNDING_CREATE]: FundingCreate,
  [STEPS.FUNDING_GUIDED]: FundingGuidedStep,
  [STEPS.FUNDING_DETAILS]: FundingDetails,
  [STEPS.FUNDING_SUPPORT_DOCS]: FundingSupportDocs,
  [STEPS.SCAN_RUNNING]: ScanRunning,
  [STEPS.SCAN_MILESTONE]: ScanMilestone,
  [STEPS.SCAN_RESULTS]: ScanResults,
  [STEPS.DRAFT_GENERATING]: DraftGenerating,
  [STEPS.DRAFT_COMPLETE]: DraftComplete,
}

function OnboardingShellContent({
  lastUpdatedBy,
}: {
  lastUpdatedBy: string | null
}) {
  const { currentStep, orgUuid, saveStatus, exitOnboarding } = useOnboardingV2()
  const { user, token, orgs, hasAgreedToTerms, acceptTerms } = getCurrentUser()
  const [wizardData, setWizardData] = useWizardData()

  const { orgData, isLoading: orgsLoading } = useCurrentUserOrgs(
    token as string,
    user,
    orgs as UUID[],
  )
  const currentOrg = orgData?.find((org) => org.uuid === orgUuid) ?? null

  const [exitDialogOpen, setExitDialogOpen] = useState(false)
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false)
  const promptTriggeredRef = useRef(false)
  // Teammate dialog: show once when another user last updated progress
  const [teammateDialogOpen, setTeammateDialogOpen] = useState(
    () => !!lastUpdatedBy && lastUpdatedBy !== user?.uuid,
  )

  // Auto-open subscribe dialog when org is created, prompt not yet shown,
  // and org is confirmed to be on the free tier (don't prompt paid orgs).
  useEffect(() => {
    if (
      !promptTriggeredRef.current &&
      orgUuid &&
      hasAgreedToTerms &&
      !wizardData.subscribePromptDismissed &&
      !orgsLoading &&
      isFreeTier(currentOrg)
    ) {
      promptTriggeredRef.current = true
      setSubscribeDialogOpen(true)
    }
  }, [
    orgUuid,
    hasAgreedToTerms,
    wizardData.subscribePromptDismissed,
    orgsLoading,
    currentOrg,
  ])

  const handleSubscribeLater = () => {
    setSubscribeDialogOpen(false)
    setWizardData({ subscribePromptDismissed: true })
  }

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [])

  const tier = getCardTier(currentStep)
  const contentWidth = getContentWidth(currentStep)
  const StepComponent = STEP_MAP[currentStep] ?? null

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: 'white',
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: 960,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
            <img
              src="/Fundsorter-h-black800px.png"
              alt="Fundsorter"
              style={{ height: 24, width: 'auto' }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <OnboardingStepperBanner />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 3 }}>
            <Box sx={{ width: 14, height: 14, flexShrink: 0 }}>
              {saveStatus === 'saving' && (
                <CircularProgress size={14} sx={{ color: COLORS.muted }} />
              )}
            </Box>
            {(currentStep >= STEPS.UPLOAD_RESOURCES ||
              !!orgUuid ||
              (orgs && orgs.length >= 1)) && (
              <Typography
                component="button"
                onClick={() => setExitDialogOpen(true)}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: COLORS.secondary,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: COLORS.primary },
                }}
              >
                Exit setup wizard
              </Typography>
            )}
            <LogoutButton />
          </Box>
        </Box>
      </Box>

      {/* Subscribe banner — above content card */}
      <Box sx={{ flexShrink: 0, pt: 2 }}>
        <OnboardingSubscribeBanner />
      </Box>

      {/* Centre: content card */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 1,
          px: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            flex: 1,
            minHeight: 0,
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // Form tiers: content starts at top
              // Other tiers: flex spacers center content vertically when it
              // fits, but collapse to zero when it overflows so the top
              // stays visible and excess scrolls off the bottom.
              ...(tier === 'form' || tier === 'wide-form'
                ? { justifyContent: 'flex-start' }
                : {
                    '&::before, &::after': { content: '""', flex: '1 0 0px' },
                  }),
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: contentWidth === 'full' ? undefined : contentWidth,
              }}
            >
              {StepComponent ? (
                <StepComponent key={currentStep} />
              ) : (
                <Typography color="error">
                  Unknown step: {currentStep}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Nav buttons */}
      <Box sx={{ flexShrink: 0, pb: 2 }}>
        <OnboardingNavButtons />
      </Box>

      {/* T&C banner — inline at bottom, replaces fixed main-app version */}
      {!hasAgreedToTerms && (
        <Box sx={{ flexShrink: 0 }}>
          <TermsAndConditionsBanner open inline onAgree={acceptTerms} />
        </Box>
      )}

      {/* Dev-only toolbar */}
      {import.meta.env.DEV &&
        import.meta.env.VITE_DEV_ONBOARD_TOOLS === 'true' && (
          <Suspense fallback={null}>
            <OnboardingQuickNav />
          </Suspense>
        )}

      {/* Subscribe dialog */}
      <SubscribeDialog
        open={subscribeDialogOpen}
        onClose={() => {
          setSubscribeDialogOpen(false)
          setWizardData({ subscribePromptDismissed: true })
        }}
        onLater={handleSubscribeLater}
      />

      {/* Exit dialog */}
      <OnboardingExitDialog
        open={exitDialogOpen}
        onClose={() => setExitDialogOpen(false)}
        onExit={exitOnboarding}
      />

      {/* Teammate dialog */}
      <TeammateDialog
        open={teammateDialogOpen}
        onContinue={() => setTeammateDialogOpen(false)}
      />
    </Box>
  )
}

// Shared by the loading placeholder and the error state below - both render
// before the real stepper (OnboardingStepperBanner) has any context to work
// from, so this static, step-1-styled stand-in keeps the logo/stepper chrome
// visually continuous across whichever one of them precedes the real wizard.
function OnboardingShellTopBar() {
  return (
    <Box
      sx={{
        flexShrink: 0,
        backgroundColor: 'white',
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: 960,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
          <img
            src="/Fundsorter-h-black800px.png"
            alt="Fundsorter"
            style={{ height: 24, width: 'auto' }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          {/* Static stepper matching step 1 appearance (no context needed) */}
          <Box sx={{ display: 'flex', width: '100%', gap: 0.5, px: 3, py: 2 }}>
            {SEGMENTS.map((segment, i) => (
              <Box
                key={segment.label}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: i === 0 ? COLORS.primary : COLORS.border,
                    opacity: i === 0 ? 0.7 : 1,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 20,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: i === 0 ? COLORS.primary : COLORS.lightGray,
                      fontWeight: i === 0 ? 600 : 400,
                      fontSize: '0.7rem',
                    }}
                  >
                    {segment.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 3 }}>
          <Box sx={{ width: 14, height: 14, flexShrink: 0 }} />
          <LogoutButton />
        </Box>
      </Box>
    </Box>
  )
}

/**
 * OnboardingShell — top-level component.
 * Handles hydration from progress record and wires save-on-nav.
 */
export default function OnboardingShell() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNewOrgFlow = searchParams.get('new') === 'true'
  const {
    user,
    token: ctxToken,
    orgs,
    isLoading: userContextLoading,
  } = getCurrentUser()
  const token = ctxToken ?? ''

  // In new-org flow, ignore existing org — start fresh.
  // Otherwise resolve via selected_org_uuid with fallback to orgs[0].
  const effectiveOrgUuid = isNewOrgFlow
    ? null
    : getEffectiveOrgUuid(user, (orgs ?? null) as UUID[] | null) || null

  // Load progress for the org (if any)
  const {
    progress,
    isLoading: progressLoading,
    isError: progressError,
  } = useOnboardingProgress(token || null, effectiveOrgUuid)

  // Hydrate localStorage from DB exactly once (not on every re-render)
  const hydratedRef = useRef(false)

  // Gate: don't mount provider until hydration data is ready on FIRST load.
  // Once the provider has mounted, never unmount it — transient changes
  // (e.g. token refresh, background refetch) must not tear down the session
  // and trigger the unmount flush with stale ref values.
  const providerMountedRef = useRef(false)

  // Clear the session exit flag when re-entering onboarding.
  // In new-org flow, also clear stale wizard data from a previous org session.
  useEffect(() => {
    localStorage.removeItem('onboarding_exited')
    if (isNewOrgFlow) {
      clearWizardData()
    }
  }, [])

  // Redirect to dashboard if onboarding is already complete, or if there's no
  // progress record at all for an org that already exists — a missing record
  // means an established org that was never meant to go through the wizard,
  // not a fresh one to onboard (skip both checks in new-org flow, and skip
  // entirely when there's no org yet, since that's a brand-new user who is
  // meant to start the wizard). Only act before the provider has mounted, and
  // only once progress has actually finished loading — otherwise this would
  // fire on the loading placeholder as well as a genuinely missing record.
  // A fetch error also leaves progress null, but that's not a confirmed
  // absence of a row - excluded here so it's handled by the error state
  // below instead of being misread as a genuinely missing record.
  useEffect(() => {
    if (providerMountedRef.current) return
    if (isNewOrgFlow || !effectiveOrgUuid || progressLoading) return
    if (progress?.is_complete || (!progress && !progressError)) {
      navigate('/', { replace: true })
    }
  }, [
    isNewOrgFlow,
    effectiveOrgUuid,
    progressLoading,
    progress?.is_complete,
    !!progress,
    progressError,
    navigate,
  ])

  // A pending invitation takes precedence over onboarding. Handles the case
  // where a newly-signed-up user verifies their email and lands on /onboarding
  // instead of the invite page. Reads localStorage directly so the per-tab
  // "used" flag can't mask an invite the user hasn't accepted yet.
  const pendingInvite = peekPostLoginRedirect()
  if (pendingInvite?.startsWith('/invite')) {
    return <Navigate to={pendingInvite} replace />
  }

  // The redirect effect above and isHydrating below both deliberately
  // exclude a progress fetch error from "confirmed no row," so neither
  // stops this case from reaching the wizard-mount path on its own. Catch
  // it here instead: show an error state, same pattern used elsewhere in
  // the app for a failed query, rather than silently starting a real
  // wizard session against the org from what was actually a fetch error.
  if (
    effectiveOrgUuid &&
    progressError &&
    !progressLoading &&
    !providerMountedRef.current
  ) {
    return (
      <Box
        sx={{
          height: '100vh',
          backgroundColor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <OnboardingShellTopBar />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ErrorMessage message="Couldn't load your onboarding status. Please try again." />
        </Box>
      </Box>
    )
  }

  // Also holds true when an existing org has no progress row at all, same as
  // the is_complete case above - both are redirect-away states, and without
  // this the provider would mount and win the race before the redirect
  // effect gets a chance to run, since there's nothing left to wait for.
  // Excludes a fetch error for the same reason as the effect above - the
  // error-state check just above already handles that case, so it must
  // not also hold up (or fall through past) hydration here.
  const isHydrating =
    !token ||
    userContextLoading ||
    (effectiveOrgUuid && progressLoading) ||
    progress?.is_complete ||
    (effectiveOrgUuid && !progressLoading && !progress && !progressError)

  if (isHydrating && !providerMountedRef.current) {
    return (
      <Box
        sx={{
          height: '100vh',
          backgroundColor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <OnboardingShellTopBar />

        {/* Content area — spinner in place of step content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <DoodleSpinner />
          <Typography sx={{ color: COLORS.secondary, fontSize: '0.95rem' }}>
            Launching setup wizard...
          </Typography>
        </Box>
      </Box>
    )
  }

  // Hydrate localStorage from DB — only once after loading completes.
  // When DB has data, overwrite localStorage (DB is authoritative).
  // When DB has no data (new org or first-time user), clear stale localStorage.
  if (!hydratedRef.current) {
    hydratedRef.current = true
    if (progress?.wizard_data) {
      writeWizardData({
        ...INITIAL_WIZARD_DATA,
        ...(progress.wizard_data as Partial<WizardData>),
      })
    } else {
      clearWizardData()
    }
  }

  providerMountedRef.current = true

  // Never restore to a progress/generating step — they fire side effects on mount.
  // Bounce to the step before (if work wasn't completed) or after (if it was).
  const rawStep = progress?.current_step ?? 1
  let resolvedStep = rawStep
  if (rawStep === STEPS.PROFILE_GENERATING) {
    resolvedStep = progress?.profile_generated
      ? STEPS.PROFILE_REVIEW
      : stepBefore(STEPS.PROFILE_GENERATING)
  } else if (rawStep === STEPS.SCAN_RUNNING) {
    const wd = progress?.wizard_data as Partial<WizardData> | undefined
    if (wd?.scanCompleted) {
      resolvedStep = STEPS.SCAN_MILESTONE
    } else if (progress?.scan_job_id) {
      // Scan was started but completion wasn't recorded (user left mid-scan).
      // Resolve to results — they'll load if the scan finished server-side.
      resolvedStep = STEPS.SCAN_RESULTS
    } else {
      resolvedStep = stepBefore(STEPS.SCAN_RUNNING)
    }
  } else if (rawStep === STEPS.DRAFT_GENERATING) {
    resolvedStep = progress?.application_id
      ? STEPS.DRAFT_COMPLETE
      : stepBefore(STEPS.DRAFT_GENERATING)
  }

  const initialState = {
    currentStep: resolvedStep,
    maxStepReached: progress?.max_step_reached ?? 1,
    orgUuid: effectiveOrgUuid,
    fundingNeedId: progress?.funding_need_id ?? null,
    scanJobId: progress?.scan_job_id ?? null,
    applicationId: progress?.application_id ?? null,
    scanResultId: progress?.scan_result_id ?? null,
    fundingQualityPassed: progress?.funding_quality_passed === true,
    staleFrom: progress?.stale_from ?? null,
    profileGenerated: progress?.profile_generated === true,
  }

  return (
    <OnboardingV2Provider initialState={initialState}>
      <OnboardingShellContent
        lastUpdatedBy={progress?.last_updated_by ?? null}
      />
    </OnboardingV2Provider>
  )
}
